import { useEffect, useState } from 'react';
import { ScanLine, Camera, Package, Boxes, Receipt, X, Printer, WifiOff, CheckCircle2, PackagePlus } from 'lucide-react';
import { ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useCapa, useSubpantalla } from '../ui/navegacionReact';
import { navegacion } from '../ui/navegacion';
import EscanerCamara from './EscanerCamara';
import MovimientoForm from './MovimientoForm';
import Miniatura from '../ui/Miniatura';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { pitidoOk, pitidoError } from '../lib/pitido';
import { ID_FICHA, abrirFichaSola } from '../alta-escaneo/ficha';
import {
  resolverOGuardar, lecturas, sincronizarLecturas, quitarLectura,
  PENDIENTE, RESUELTA,
} from '../lib/colaLecturas';

// `contexto.codigo` llega desde la búsqueda global. Escanear un QR y
// encontrar la misma pieza tecleando su serie acaban en la misma ficha:
// es la misma pregunta hecha por dos caminos.
// P17 · `contexto.tipo` (ENTRADA, SALIDA o TRASPASO) llega desde los atajos
// del inicio del teléfono: la pantalla lo dice arriba y el formulario nace
// con ese movimiento elegido. Sin él, Escanear es el de siempre.
const POR_TIPO = {
  ENTRADA: { titulo: 'Entrada', sub: 'Escanea el producto que entra.' },
  SALIDA: { titulo: 'Salida', sub: 'Escanea el producto que sale.' },
  TRASPASO: { titulo: 'Traspaso', sub: 'Escanea el producto que cambias de lugar.' },
};

// U-02 fase b · `Camara` es el componente de la cámara (las pruebas pasan uno
// que lee al instante). Escanear sabe por dónde llegó cada lectura —gatillo
// LECTOR, cámara CAMARA, el campo TECLADO, un enlace o un código que llega
// ya elegido URL— y lo pasa al movimiento con el `codigo_escaneado` que
// devolvió el servidor.
export default function Escanear({ contexto, irA, Camara = EscanerCamara }) {
  const { tieneNivel, puede } = useAuth();
  // U-03 fase a · «Darlo de alta» en el 404: solo con el permiso `catalogo`,
  // el mismo con que el servidor promete el alta en el texto del 404 y con
  // que el menú enseña «Dar de alta desde el código».
  const puedeDarDeAlta = typeof puede === 'function' && puede('catalogo');
  // ALTA-ESC · un código de barras que nadie tiene abre la Ficha previa solo
  // (alta-escaneo), con el mismo permiso y nivel que su menú.
  const puedeAltaEscaneando = puedeDarDeAlta && typeof tieneNivel === 'function' && tieneNivel('ENCARGADO');
  const modo = POR_TIPO[contexto?.tipo] ? contexto.tipo : null;
  const [codigo, setCodigo] = useState(contexto?.codigo || '');
  const [camara, setCamara] = useState(false);
  useCapa(camara, () => setCamara(false));         // atrás cierra la cámara, y al desmontarse se apaga
  const [resuelto, setResuelto] = useState(null);
  const [medio, setMedio] = useState(contexto?.codigo ? (contexto.medio || 'URL') : null);
  const [error, setError] = useState(null);
  // U-03 fase a · el código que el servidor no reconoció (404), para abrir
  // el alta con él ya puesto. El texto del 404 no cambia: se pinta tal cual.
  const [sinAlta, setSinAlta] = useState(null);
  // Arranca en "cargando" cuando se entra con un código ya elegido: así el
  // efecto no tiene que fijar estado en su cuerpo para contarlo.
  const [cargando, setCargando] = useState(Boolean(contexto?.codigo));
  const [tecleando, setTecleando] = useState(false);
  // AC-5 · lo que se avisa cuando la lectura quedó guardada sin señal, y
  // con qué tipo de movimiento se abre una lectura guardada al volver.
  const [guardada, setGuardada] = useState(null);
  const [tipoAbierto, setTipoAbierto] = useState(null);
  // Cambia cada vez que se guarda una lectura: la lista la vuelve a leer.
  const [version, setVersion] = useState(0);
  // El resultado es una subpantalla: atrás vuelve a escanear. El que llega
  // ya elegido desde otra pantalla (`contexto.codigo`) no: esa entrada ya
  // lo representa, y atrás vuelve a donde se estaba.
  const [deContexto, setDeContexto] = useState(false);
  useSubpantalla('resultado', !!resuelto && !deContexto, () => reiniciar());

  useEffect(() => {
    const inicial = contexto?.codigo;
    if (!inicial) return;
    let vigente = true;
    resolverOGuardar(inicial, { tipo: modo, medio: contexto.medio || 'URL' })
      .then((r) => {
        if (!vigente) return;
        setMedio(contexto.medio || 'URL');
        if (r.estado === 'resuelta') { setDeContexto(true); setResuelto(r.datos); }
        else { setGuardada(r.lectura.codigo); setVersion((v) => v + 1); }
      })
      .catch((e) => {
        if (!vigente) return;
        if (e instanceof ErrorApi && e.status === 404 && irA
            && abrirFichaSola(String(inicial).trim(), { modo, puedeAlta: puedeAltaEscaneando })) {
          irA(ID_FICHA, { codigo: String(inicial).trim(), medio: contexto.medio || 'URL' });
          return;
        }
        setError(e instanceof ErrorApi ? e.message : 'No se pudo resolver el código.');
        setSinAlta(e instanceof ErrorApi && e.status === 404 ? String(inicial).trim() : null);
      })
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
    // `irA` y el permiso fuera a propósito (ALTA-ESC): la pantalla que la
    // abre redefine `irA` en cada pintado, y eso resolvería el código otra vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexto?.codigo, contexto?.medio, modo]);

  async function resolver(valor, medioDeLectura = 'TECLADO') {
    const c = (valor || '').trim();
    // Pulsar «Ir» sin nada escrito no puede quedarse mudo (auditoría de
    // botones, 19 sep 2026): se dice qué hace falta.
    if (!c) { setError('Escribe o escanea un código primero: el SKU, el número de serie o el código de la etiqueta.'); return; }
    setError(null);
    setSinAlta(null);
    setGuardada(null);
    setResuelto(null);
    setMedio(medioDeLectura);
    setCargando(true);
    try {
      // AC-5 · sin señal, la lectura se guarda en el teléfono en vez de
      // perderse. Con señal, es la misma consulta de siempre.
      const r = await resolverOGuardar(c, { tipo: modo, medio: medioDeLectura });
      if (r.estado === 'resuelta') {
        setTipoAbierto(null);
        setResuelto(r.datos);
      } else {
        setGuardada(r.lectura.codigo);
        setCodigo('');
        setVersion((v) => v + 1);
      }
    } catch (e) {
      if (e instanceof ErrorApi && e.status === 404 && irA
          && abrirFichaSola(c, { modo, puedeAlta: puedeAltaEscaneando })) {
        irA(ID_FICHA, { codigo: c, medio: medioDeLectura });
        return;
      }
      pitidoError();
      setError(e instanceof ErrorApi ? e.message : 'No se pudo resolver el código.');
      setSinAlta(e instanceof ErrorApi && e.status === 404 ? c : null);
    } finally {
      setCargando(false);
    }
  }

  // GATILLO FÍSICO. Los terminales de almacén (Zebra, Honeywell y
  // similares) no traen cámara que valga la pena: traen un gatillo que
  // teclea el código como si fuera un teclado. Hasta ahora había que tocar
  // antes el campo de texto —con guantes y una caja en la otra mano—, o
  // usar la cámara, que en un pasillo mal iluminado es más lenta.
  //
  // Se apaga mientras alguien escribe en el campo: un lector ES un
  // teclado, y sus dígitos entrarían ahí además de resolverse.
  useEscanerTeclado(
    (leido, { medio: m } = {}) => {
      pitidoOk();
      setCodigo(leido);
      resolver(leido, m || 'LECTOR');
    },
    { activo: !resuelto && !tecleando },
  );

  function reiniciar() {
    setResuelto(null);
    setDeContexto(false);
    setCodigo('');
    setError(null);
    setSinAlta(null);
    setGuardada(null);
    setTipoAbierto(null);
  }

  // Abrir una lectura guardada que ya volvió resuelta: es el mismo
  // resultado que si se hubiera escaneado con señal.
  async function abrirGuardada(l) {
    await quitarLectura(l.clave_idempotencia);
    setGuardada(null);
    setError(null);
    setTipoAbierto(l.tipo || null);
    setMedio(l.medio || null);
    setResuelto(l.respuesta);
  }

  if (resuelto) {
    // «Escanear otro» con un movimiento a medias pregunta igual que atrás
    // (TC-DR-AT); tras registrarlo ya no hay nada que preguntar.
    return <Resultado datos={resuelto} medio={medio} irA={irA} onCerrar={() => navegacion.conGuardia(reiniciar)} puedeMover={tieneNivel('OPERADOR')} tipoInicial={tipoAbierto || modo} />;
  }

  return (
    <div className="space-y-4">
      <div className="text-center pt-2">
        <div className="inline-flex bg-[var(--color-marca)] text-white rounded-2xl p-4 mb-2">
          <ScanLine className="h-8 w-8" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{modo ? POR_TIPO[modo].titulo : 'Escanear'}</h2>
        {modo && <p className="text-sm font-semibold text-gray-700">{POR_TIPO[modo].sub}</p>}
        <p className="text-sm text-gray-500">
          {tecleando
            ? 'Teclea el código; el gatillo está en pausa.'
            : 'Dispara el gatillo, usa la cámara, o teclea el código.'}
        </p>
      </div>

      {camara ? (
        <Camara
          onCodigo={(t) => { setCamara(false); setCodigo(t); resolver(t, 'CAMARA'); }}
          onCerrar={() => setCamara(false)}
        />
      ) : (
        <button
          onClick={() => setCamara(true)}
          className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-[var(--color-marca)] font-semibold py-4 rounded-xl"
        >
          <Camera className="h-5 w-5" /> Escanear con cámara
        </button>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); resolver(codigo, 'TECLADO'); }}
        className="flex gap-2"
      >
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          onFocus={() => setTecleando(true)}
          onBlur={() => setTecleando(false)}
          placeholder="SKU o código…"
          className="entrada"
          autoCapitalize="characters"
        />
        <button
          type="submit"
          disabled={cargando}
          className="bg-[var(--color-marca)] text-white font-semibold px-5 rounded-xl disabled:opacity-60"
        >
          {cargando ? '…' : 'Ir'}
        </button>
      </form>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5 break-words">{error}</div>}

      {/* U-03 fase a · LA PROMESA DEL 404, CUMPLIDA. El servidor dice «Puedes
          darlo de alta ahora mismo» a quien tiene `catalogo`; este es el
          botón. Abre «Dar de alta desde el código» con el código puesto. */}
      {error && sinAlta && puedeDarDeAlta && irA && (
        <button
          type="button"
          onClick={() => irA('gtin-alta', { codigo: sinAlta, medio })}
          className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4"
        >
          <PackagePlus className="h-5 w-5" aria-hidden="true" /> Darlo de alta
        </button>
      )}

      {guardada && (
        <div role="status" className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5">
          Sin conexión: el código <span className="font-mono font-semibold">{guardada}</span> quedó
          guardado en este teléfono. Se manda solo cuando vuelva la conexión; puedes seguir escaneando.
        </div>
      )}

      <LecturasGuardadas version={version} onAbrir={abrirGuardada} />
    </div>
  );
}

// AC-5 · LAS LECTURAS GUARDADAS EN EL TELÉFONO. Lo pendiente se ve en
// amarillo (atención: todavía no llegó), lo que ya se mandó en verde, con
// el botón para terminar el trabajo. Mientras la pantalla está abierta, la
// lista se mira cada pocos segundos y, si hay señal, se mandan las
// pendientes (el envío es uno solo aunque lo pida también la barra de la
// app: `sincronizarLecturas` lo comparte).
const CADA_MS = 3000;

function LecturasGuardadas({ version, onAbrir }) {
  const [lista, setLista] = useState([]);

  useEffect(() => {
    let vivo = true;
    const mirar = () => sincronizarLecturas()
      .catch(() => null)
      .then(() => lecturas())
      .then((l) => { if (vivo) setLista(l); })
      .catch(() => {});
    mirar();
    window.addEventListener('online', mirar);
    const iv = setInterval(mirar, CADA_MS);
    return () => { vivo = false; window.removeEventListener('online', mirar); clearInterval(iv); };
  }, [version]);

  async function quitar(l) {
    await quitarLectura(l.clave_idempotencia);
    setLista((prev) => prev.filter((x) => x.clave_idempotencia !== l.clave_idempotencia));
  }

  const pendientes = lista.filter((l) => l.estado === PENDIENTE);
  const mandadas = lista.filter((l) => l.estado !== PENDIENTE);
  if (!pendientes.length && !mandadas.length) return null;

  return (
    <div className="space-y-3" data-lecturas-guardadas>
      {pendientes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
            <WifiOff className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>
              {pendientes.length === 1
                ? '1 lectura guardada en este teléfono; se manda sola al volver la conexión.'
                : `${pendientes.length} lecturas guardadas en este teléfono; se mandan solas al volver la conexión.`}
            </span>
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {pendientes.map((l) => (
              <li key={l.clave_idempotencia}
                  className="text-xs font-mono font-semibold px-2 py-1 rounded bg-white border border-amber-200 text-amber-900 break-all">
                {l.codigo}
              </li>
            ))}
          </ul>
        </div>
      )}

      {mandadas.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
          <p className="flex items-start gap-2 text-sm font-semibold text-green-900">
            <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>
              {mandadas.length === 1
                ? 'Se mandó 1 lectura guardada. Tócala para terminar.'
                : `Se mandaron ${mandadas.length} lecturas guardadas. Toca cada una para terminar.`}
            </span>
          </p>
          <ul className="space-y-2">
            {mandadas.map((l) => (
              <li key={l.clave_idempotencia} className="bg-white border border-gray-200 rounded-xl p-2.5 space-y-2">
                <div className="text-xs font-mono text-gray-500 break-all">{l.codigo}</div>
                {l.estado === RESUELTA ? (
                  <button
                    type="button"
                    onClick={() => onAbrir(l)}
                    className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-3 py-2"
                  >
                    <Package className="h-5 w-5" aria-hidden="true" />
                    Abrir {nombreDe(l.respuesta)}
                  </button>
                ) : (
                  <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2">{l.mensaje}</p>
                )}
                <button
                  type="button"
                  onClick={() => quitar(l)}
                  className="w-full min-h-11 inline-flex items-center justify-center gap-1 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl"
                >
                  <X className="h-4 w-4" aria-hidden="true" /> Quitar de esta lista
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function nombreDe(r) {
  if (!r) return 'lectura';
  if (r.tipo === 'ORDEN') return `pedido ${r.codigo}`;
  if (r.tipo === 'PIEZA') return r.datos?.producto || 'pieza';
  return r.datos?.nombre || r.datos?.sku || 'producto';
}

function Resultado({ datos, medio, onCerrar, puedeMover, irA, tipoInicial }) {
  return (
    <div className="space-y-4">
      <button onClick={onCerrar} className="inline-flex items-center gap-1 min-h-11 text-sm font-semibold text-gray-500">
        <X className="h-4 w-4" /> Escanear otro
      </button>

      {datos.tipo === 'PRODUCTO' && (
        <>
          <Ficha
            icono={Package}
            titulo={datos.datos.nombre}
            sub={datos.datos.sku}
            extra={`Existencia: ${Number(datos.datos.existencia_total ?? 0)} ${datos.datos.unidad_medida}`}
            estado={datos.datos.estado}
            imagen={datos.datos.imagen}
          />
          {/* P16 · «IMPRIMIR ETIQUETA» DONDE ESTÁ EL PRODUCTO. Antes había que
              ir a Etiquetas QR, buscarlo, marcarlo y abrir la hoja (5 toques,
              LISTO j hallazgo 3). Ahora lleva a esa misma pantalla con este
              producto ya elegido y la hoja abierta: queda «Bajar el PDF». */}
          {irA && (
            <button
              type="button"
              onClick={() => irA('etiquetas', { sku: datos.datos.sku, imprimir: true })}
              className="w-full inline-flex items-center justify-center gap-2 min-h-12
                         bg-white border border-gray-300 text-gray-800 font-semibold
                         rounded-xl py-3"
            >
              <Printer className="h-5 w-5" aria-hidden="true" /> Imprimir etiqueta
            </button>
          )}
          {puedeMover ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <MovimientoForm producto={datos.datos}
                              presentacion={datos.presentacion}
                              tipoInicial={tipoInicial}
                              codigoEscaneado={datos.codigo_escaneado || null}
                              medio={medio}
                              onListo={onCerrar} />
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center">Tu rol solo permite consultar.</p>
          )}
        </>
      )}

      {datos.tipo === 'PIEZA' && (
        <Ficha
          icono={Boxes}
          titulo={datos.datos.producto}
          sub={`Serie ${datos.datos.serie}`}
          extra={`Ubicación: ${datos.datos.ubicacion || '—'} · ${datos.datos.disponibilidad}`}
          estado={datos.datos.estado_fisico}
          imagen={datos.datos.imagen}
        />
      )}

      {datos.tipo === 'ORDEN' && (
        <div className="space-y-2">
          <Ficha
            icono={Receipt}
            titulo="Pedido de cliente"
            sub={datos.codigo}
            extra={datos.motivo_no_cobrable
              || (datos.completo
                ? 'Está completo: hay de todo lo que pidió.'
                : 'Falta mercancía. En el mostrador verás qué y cuánto.')}
            estado={datos.motivo_no_cobrable ? 'rojo'
              : (datos.completo ? 'verde' : 'amarillo')}
          />
          {/* EL BOTON QUE FALTABA. Hasta el Bloque K, escanear un ORD- solo
              decia «el cobro B2C aun no esta disponible»: se identificaba
              el codigo y ahi terminaba todo. Ahora lleva al mostrador con
              los renglones puestos.
              No sale si el pedido no se puede cobrar —ya cobrado, vencido o
              cancelado—: un boton que no va a funcionar se pulsa una vez y
              después ya no se cree ninguno. */}
          {!datos.motivo_no_cobrable && (
            <button
              type="button"
              onClick={() => irA('pos', { folio_pedido: datos.codigo })}
              className="w-full inline-flex items-center justify-center gap-2
                         bg-[var(--color-marca)] text-white font-semibold
                         rounded-xl py-4"
            >
              <Receipt className="h-5 w-5" aria-hidden="true" /> Cobrar este pedido
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Ficha({ icono: Icono, titulo, sub, extra, estado, imagen }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-start gap-3">
      {/* Lo que de verdad justifica la foto: quien escanea tiene la pieza en
          la mano y necesita comprobar que es la que el sistema cree, ANTES
          de confirmar la salida. */}
      {imagen ? (
        <Miniatura src={imagen} nombre={titulo} tamano="md" />
      ) : (
        <div className="bg-[var(--color-marca-suave)] text-[var(--color-marca)] rounded-xl p-3">
          <Icono className="h-6 w-6" />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{titulo}</div>
        <div className="text-xs font-mono text-gray-500">{sub}</div>
        {extra && <div className="text-sm text-gray-700 mt-1">{extra}</div>}
        {estado && (
          <span className="inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600">
            {estado}
          </span>
        )}
      </div>
    </div>
  );
}
